// Firebase Authentication Context
// Provides Firebase auth state and user data

"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { User as FirebaseUser } from "firebase/auth"
import { onAuthChange, ensureUserExists } from "./firebase/auth"
import { User } from "./user-context"
import { useRouter } from "next/navigation"

interface FirebaseAuthContextType {
  firebaseUser: FirebaseUser | null
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  signOut: () => Promise<void>
}

const FirebaseAuthContext = createContext<FirebaseAuthContextType>({
  firebaseUser: null,
  user: null,
  loading: false,
  isAuthenticated: false,
  signOut: async () => {},
})

export function FirebaseAuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    console.log("[FirebaseAuth] Subscribing to auth state")
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      console.log("[FirebaseAuth] onAuthChange:", firebaseUser ? `uid=${firebaseUser.uid}` : "signed out")
      setFirebaseUser(firebaseUser)

      if (firebaseUser) {
        try {
          console.log("[FirebaseAuth] Ensuring user exists in Firestore...")
          const userData = await ensureUserExists(firebaseUser)
          console.log("[FirebaseAuth] User data loaded:", userData ? `id=${userData.id}` : "null")
          setUser(userData)
        } catch (error) {
          console.error("[FirebaseAuth] Error loading user data:", error)
          if (error && typeof (error as any)?.code !== "undefined") {
            console.error("[FirebaseAuth] Firestore code:", (error as any).code, "message:", (error as any).message)
          }
          setUser(null)
        }
      } else {
        setUser(null)
      }

      setLoading(false)
      console.log("[FirebaseAuth] Loading complete")
    })

    return () => unsubscribe()
  }, [])

  const signOut = async () => {
    const { signOut: firebaseSignOut } = await import("./firebase/auth")
    await firebaseSignOut()
    setUser(null)
    router.push("/login")
  }

  return (
    <FirebaseAuthContext.Provider
      value={{
        firebaseUser,
        user,
        loading,
        isAuthenticated: !!firebaseUser && !!user,
        signOut,
      }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  )
}

export function useFirebaseAuth() {
  const context = useContext(FirebaseAuthContext)
  if (!context) {
    throw new Error("useFirebaseAuth must be used within FirebaseAuthProvider")
  }
  return context
}

